import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import { publicApi } from "../../utils/util";
import { SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import SwiperKlass from "component/klass/SwiperKlass";
import Loading from "component/Loading";
import TopicsMenu from "component/klass/TopicsMenu";
import CourseCard from "component/klass/CourseCard";
import HorizontalCourseCard from "component/klass/HorizontalCourseCard";
import CoursesQuickSearchForm from "component/klass/CoursesQuickSearchForm";
import CoursesSearchForm from "component/klass/CoursesSearchForm";
import { Course, CourseList, Curation } from "types/klass";
import { CLASS_ROUTE } from "@/constant/urlConstant";

import ClassBanner from "component/advertisment/banner";
import { useProducts } from "api/market/product/hooks";
import { Product } from "types/market";
import { useRecoilValue } from "recoil";
import { tokenState } from "state";
import { useEffect } from "react";
import { getClassPublicCurationDetailApi } from "api/class/curation/api";
import { useFreeCourses } from "api/klass/hooks";
import { useChannelList } from "@/api/classChannel/hooks";
import { isMo, cls } from "utils/util";
import RecommendedChannels from "@/component/klass/RecommendedChannels";

const Klass = () => {
  const token = useRecoilValue(tokenState);
  const isMobile = isMo();

  // FIXME: ReactQuery 아직 학습이 필요합니다. Curation 모델 정의 해야합니다.
  const { data: curations, isLoading: curationsLoading } = useQuery<Curation[] | undefined, Error>(
    "KlassCurations",
    () =>
      publicApi?.get("/class/public/curations").then((res) => {
        return res.data.curations;
      }),
    {
      refetchOnWindowFocus: false,
    },
  );

  // MARK: 무료 강의
  const { data: freeCourses, isLoading: freeCoursesLoading } = useFreeCourses(3);

  // MARK: 강의 채널 목록
  const { data: channelList, isLoading: isLoadingChannelList } = useChannelList();

  const curationProductIds = curations
    ?.reduce((acc: string[], cur) => {
      return [...acc, ...(cur.courses?.map((course) => course?.products[0]?.productId) || [])];
    }, [])
    .filter((id) => id);

  const { data: products } = useProducts(
    { ids: curationProductIds },
    {
      select: (response: { hits: Product[] }) => {
        return response?.hits || [];
      },
      enabled: !!token?.uid && (curationProductIds || [])?.length > 0,
    },
  ) as { data: Product[]; isLoading: boolean };

  const isProductSoldOut = (productId: string) => {
    return products?.find((product) => product.id === productId)?.is_sold_out;
  };

  useEffect(() => {
    if (curations) {
      getClassPublicCurationDetailApi({ curationId: curations[0].curationId });
    }
  }, [curations]);

  const isRecommendedChannelsVisible = !!channelList && (channelList.channels.length ?? 0) >= 3;

  if (!curations || curationsLoading || freeCoursesLoading || isLoadingChannelList)
    return <Loading small />;

  return (
    <div className="relative flex flex-col items-center">
      <div className="w-full max-w-[1200px] lg:px-4 lg:pt-4 xl:px-0">
        <div className="hidden lg:block">
          <ClassBanner name="pc_Klass_top" page="Klass" />
        </div>
        <TopicsMenu />
        <hr className="hidden lg:block h-[1px] bg-gray-200 border-0" />
      </div>
      <div className="w-full max-w-[1200px] px-4 xl:px-0">
        <div className="lg:mt-5 lg:mb-10">
          <div className="flex flex-col lg:flex-row items-center">
            <div className="w-full my-[12px] lg:flex-1 lg:mr-2.5 lg:my-0">
              <Link
                className="flex items-center justify-center gap-[5px] h-[44px] lg:w-[134px] p-[10px] rounded-md bg-secondary-800 text-[15px] font-semibold leading-[22px] tracking-[-0.1px] text-white whitespace-nowrap hover:bg-secondary-900"
                to={`/mypage/${CLASS_ROUTE}`}
              >
                <i className="ii ii-user-screen-outline text-[24px] text-white"></i>내 강의실 이동
              </Link>
            </div>
            <div className="hidden lg:flex flex-0 pr-2">
              <CoursesQuickSearchForm />
            </div>
            <div className="flex-0 border border-medi-border-primary w-full lg:w-[328px] my-[12px] lg:my-0 rounded-[5px]">
              <CoursesSearchForm />
            </div>
          </div>
          <div className="flex lg:hidden overflow-auto whitespace-nowrap border-t border-b border-medi-border-primary py-[12px] mx-[-16px] pl-[16px]">
            <CoursesQuickSearchForm />
          </div>
        </div>

        {/* MARK: 첫번째 큐레이션 / 무료 강의 배치 */}
        <div className="mb-10 lg:mb-14 grid grid-cols-1 lg:grid-cols-2 gap-[43px]">
          {/* MARK: 첫번째 큐레이션 */}
          {curations[0] && (
            <div className="w-full">
              <div className="flex items-center justify-between py-[14px]">
                <h3 className="leading-[36px] tracking-[-0.14px] text-medi-text-primary text-[24px] font-bold">
                  {curations[0].title}
                </h3>
                <Link className="block" to={`/${CLASS_ROUTE}/curation/${curations[0].curationId}`}>
                  <span className="flex items-center justify-center whitespace-nowrap rounded-[4px] px-[8px] py-[4px] text-[13px] font-semibold tracking-[-0.33px] leading-[20px] text-secondary-700 hover:cursor-pointer hover:bg-gray-200 hover:text-secondary-800">
                    <i className="ii ii-plus ii-weight-600 text-secondary-800 mr-0.5 text-[12px]" />
                    더보기
                  </span>
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-x-[11px] gap-y-[24px]">
                {curations[0].courses?.slice(0, 4).map((item) => {
                  const course =
                    item.courses?.find((course) => course.courseId === item.defaultCourseId) ||
                    item;
                  return (
                    <Link
                      className="block"
                      key={course.courseId}
                      to={`/${CLASS_ROUTE}/course/${course.courseId}`}
                    >
                      <CourseCard
                        thumbnail={course.thumbnailImage.link}
                        lecturers={course.lecturerNames}
                        title={course.title}
                        regularPrice={course.products[0]?.regularPrice}
                        salePrice={course.products[0]?.salePrice}
                        topic={course.groupedBadges.topics}
                        topicCategory={course.groupedBadges.topicCategory}
                        level={course.groupedBadges.level?.join(",")}
                        isFree={course.isFree}
                        isSoldOut={isProductSoldOut(
                          (course as Course).products[0]?.productId || "",
                        )}
                        channel={(course as Course).channel}
                      />
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* MARK: 무료 강의 섹션 */}
          {freeCourses && freeCourses.length > 0 && (
            <div className="w-full">
              {/* MARK: 무료 강의 배너 이미지 */}
              <div className="mb-[16px] sm:mb-[12px] relative mx-[-16px] lg:mx-0">
                <img
                  src="/img/klass/klass-home-free-banner.png"
                  alt="무료 강의 배너"
                  className="w-full lg:h-[246px] lg:rounded-md"
                />
                {/* MARK: 배너 위 오버레이 컨테이너 */}
                <div
                  className={cls(
                    "p-[16px] absolute top-0 left-0 w-full h-full gap-[16px] text-center flex flex-col items-center justify-center",
                    "sm:justify-between sm:gap-[10px] sm:text-left sm:w-auto sm:h-auto sm:bottom-[26px] sm:left-[26px] sm:px-0 sm:right-[26px] sm:flex-row sm:items-end",
                  )}
                >
                  {/* MARK: 배너 위 오버레이 텍스트 */}
                  <p className="text-[20px] sm:text-[28px] font-bold text-white tracking-[-0.12px] sm:tracking-[-0.14px] leading-[30px] sm:leading-[42px]">
                    모어덴에서 제공하는 <br />
                    놓치지 말아야 할 무료 강의
                  </p>
                  {/* MARK: 배너 위 오버레이 버튼 */}
                  <Link
                    to={`/${CLASS_ROUTE}/search?free=true`}
                    className="w-full sm:w-auto whitespace-nowrap justify-center font-bold text-white text-[14px] leading-[20px] tracking-[-0.1px] px-[14px] py-[10px] flex items-center gap-[4px] rounded-[999px] border border-[rgba(255,255,255,0.10)] bg-[rgba(255,255,255,0.30)] hover:bg-[rgba(255,255,255,0.40)] backdrop-blur-[4px]"
                  >
                    무료 강의 더보기
                    <i className="ii ii-arrow-right ii-weight-600 text-white text-[16px]" />
                  </Link>
                </div>
              </div>
              {/* MARK: 무료 강의 카드 3개 배치 */}
              <div className="flex flex-col gap-[16px] sm:gap-[8px]">
                {freeCourses.slice(0, 3).map((course) => (
                  <Link
                    className="block"
                    key={course.id}
                    to={`/${CLASS_ROUTE}/course/${course.id}`}
                  >
                    <HorizontalCourseCard
                      thumbnail={course.thumbnailImageUrl}
                      lecturers={course.lecturers}
                      title={course.title}
                      regularPrice={course?.product?.regularPrice}
                      salePrice={course?.product?.salePrice}
                      topic={course?.groupedBadges?.topics}
                      topicCategory={course?.groupedBadges?.topicCategory}
                      level={course?.groupedBadges?.level?.join(",")}
                      isFree={course.isFree}
                      imageWidth={isMobile || window.innerWidth < 1024 ? "128px" : "224px"}
                      channel={(course as CourseList).channel}
                      isShowClassNameBox={!isMobile}
                    />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* MARK: 배너 섹션 */}
        <div className="mb-10 lg:mb-14 md:grid grid grid-flow-row md:grid-flow-col md:grid-cols-3 gap-5 md:gap-5 lg:gap-11">
          <ClassBanner name="pc_Klass_middle_left" page="Klass" width="full" />
          <ClassBanner name="pc_Klass_middle_center" page="Klass" width="full" />
          <ClassBanner name="pc_Klass_middle_right" page="Klass" width="full" />
        </div>

        {/* MARK: 두번째 큐레이션 */}
        {curations[1] && (
          <div className="mb-10 lg:mb-14">
            <div className="flex items-center justify-between py-[14px]">
              <h3 className="leading-[36px] tracking-[-0.14px] text-medi-text-primary text-[24px] font-bold">
                {curations[1].title}
              </h3>
              <Link className="block" to={`/${CLASS_ROUTE}/curation/${curations[1].curationId}`}>
                <span className="flex items-center justify-center whitespace-nowrap rounded-[4px] px-[8px] py-[4px] text-[13px] font-semibold tracking-[-0.33px] leading-[20px] text-secondary-700 hover:cursor-pointer hover:bg-gray-200 hover:text-secondary-800">
                  <i className="ii ii-plus ii-weight-600 text-secondary-800 mr-0.5 text-[12px]" />
                  더보기
                </span>
              </Link>
            </div>
            <SwiperKlass navigationTop="top-[67px]">
              {curations[1].courses?.map((item) => {
                const course =
                  item.courses?.find((course) => course.courseId === item.defaultCourseId) || item;
                return (
                  <SwiperSlide key={course.courseId}>
                    <Link className="block" to={`/${CLASS_ROUTE}/course/${course.courseId}`}>
                      <CourseCard
                        thumbnail={course.thumbnailImage.link}
                        lecturers={course.lecturerNames}
                        title={course.title}
                        regularPrice={course.products[0]?.regularPrice}
                        salePrice={course.products[0]?.salePrice}
                        topic={course.groupedBadges.topics}
                        topicCategory={course.groupedBadges.topicCategory}
                        level={course.groupedBadges.level?.join(",")}
                        isSwiper
                        isFree={course.isFree}
                        channel={(course as Course).channel}
                      />
                    </Link>
                  </SwiperSlide>
                );
              })}
            </SwiperKlass>
          </div>
        )}

        {/* MARK: 배너 섹션 */}
        <div className="hidden lg:block mb-10 lg:mb-14">
          <ClassBanner name="pc_Klass_middle_second" page="Klass" />
        </div>

        {/* MARK: 세번째 큐레이션 */}
        {curations[2] && (
          <div className="mb-10 lg:mb-14">
            <div className="flex items-center justify-between py-[14px]">
              <h3 className="leading-[36px] tracking-[-0.14px] text-medi-text-primary text-[24px] font-bold">
                {curations[2].title}
              </h3>
              <Link className="block" to={`/${CLASS_ROUTE}/curation/${curations[2].curationId}`}>
                <span className="flex items-center justify-center whitespace-nowrap rounded-[4px] px-[8px] py-[4px] text-[13px] font-semibold tracking-[-0.33px] leading-[20px] text-secondary-700 hover:cursor-pointer hover:bg-gray-200 hover:text-secondary-800">
                  <i className="ii ii-plus ii-weight-600 text-secondary-800 mr-0.5 text-[12px]" />
                  더보기
                </span>
              </Link>
            </div>
            <SwiperKlass navigationTop="top-[67px]">
              {curations[2].courses?.map((item) => {
                const course =
                  item.courses?.find((course) => course.courseId === item.defaultCourseId) || item;
                return (
                  <SwiperSlide key={course.courseId}>
                    <Link className="block" to={`/${CLASS_ROUTE}/course/${course.courseId}`}>
                      <CourseCard
                        thumbnail={course.thumbnailImage.link}
                        lecturers={course.lecturerNames}
                        title={course.title}
                        regularPrice={course.products[0]?.regularPrice}
                        salePrice={course.products[0]?.salePrice}
                        topic={course.groupedBadges.topics}
                        topicCategory={course.groupedBadges.topicCategory}
                        level={course.groupedBadges.level?.join(",")}
                        isSwiper
                        isFree={course.isFree}
                        channel={(course as Course).channel}
                      />
                    </Link>
                  </SwiperSlide>
                );
              })}
            </SwiperKlass>
          </div>
        )}
      </div>

      {/* MARK: 채널 섹션 */}
      {isRecommendedChannelsVisible && (
        <section className="w-full mb-10 lg:mb-14">
          <RecommendedChannels channelListResponse={channelList} page="클래스 홈" />
        </section>
      )}

      <div className="w-full max-w-[1200px] px-4 xl:px-0">
        {/* MARK: 나머지 큐레이션 */}
        {curations
          ?.filter((curation, index) => curation && index > 2)
          .map((curation) => {
            return (
              <div className="mb-10 lg:mb-14" key={curation.curationId}>
                <div className="flex items-center justify-between py-[14px]">
                  <h3 className="leading-[36px] tracking-[-0.14px] text-medi-text-primary text-[24px] font-bold">
                    {curation.title}
                  </h3>
                  <Link className="block" to={`/${CLASS_ROUTE}/curation/${curation.curationId}`}>
                    <span className="flex items-center justify-center whitespace-nowrap rounded-[4px] px-[8px] py-[4px] text-[13px] font-semibold tracking-[-0.33px] leading-[20px] text-secondary-700 hover:cursor-pointer hover:bg-gray-200 hover:text-secondary-800">
                      <i className="ii ii-plus ii-weight-600 text-secondary-800 mr-0.5 text-[12px]" />
                      더보기
                    </span>
                  </Link>
                </div>
                <div className="hidden lg:block">
                  <SwiperKlass navigationTop="top-[67px]">
                    {curation.courses?.map((item) => {
                      const course =
                        item.courses?.find((course) => course.courseId === item.defaultCourseId) ||
                        item;
                      return (
                        <SwiperSlide key={course.courseId}>
                          <Link className="block" to={`/${CLASS_ROUTE}/course/${course.courseId}`}>
                            <CourseCard
                              thumbnail={course.thumbnailImage.link}
                              lecturers={course.lecturerNames}
                              title={course.title}
                              regularPrice={course.products[0]?.regularPrice}
                              salePrice={course.products[0]?.salePrice}
                              topic={course.groupedBadges.topics}
                              topicCategory={course.groupedBadges.topicCategory}
                              level={course.groupedBadges.level?.join(",")}
                              isSwiper
                              isFree={course.isFree}
                              channel={(course as Course).channel}
                            />
                          </Link>
                        </SwiperSlide>
                      );
                    })}
                  </SwiperKlass>
                </div>
                <div className="lg:hidden">
                  <SwiperKlass navigationTop="top-[67px]">
                    {curation.courses?.map((item) => {
                      const course =
                        item.courses?.find((course) => course.courseId === item.defaultCourseId) ||
                        item;
                      return (
                        <SwiperSlide key={course.courseId}>
                          <Link className="block" to={`/${CLASS_ROUTE}/course/${course.courseId}`}>
                            <CourseCard
                              thumbnail={course.thumbnailImage.link}
                              lecturers={course.lecturerNames}
                              title={course.title}
                              regularPrice={course.products[0]?.regularPrice}
                              salePrice={course.products[0]?.salePrice}
                              topic={course.groupedBadges.topics}
                              topicCategory={course.groupedBadges.topicCategory}
                              level={course.groupedBadges.level?.join(",")}
                              isFree={course.isFree}
                              isSwiper
                              channel={(course as Course).channel}
                            />
                          </Link>
                        </SwiperSlide>
                      );
                    })}
                  </SwiperKlass>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default Klass;
